import { isEmpty, isNil, groupBy } from "ramda";
import {
  CHECKLIST_CLIENT as checklistClient,
  TemplateRevId,
  getCurrentPosition,
} from "../common";

const transformImageFileToBase64Data = async (file) => {
  const reader = new FileReader();
  return new Promise((resolve, reject) => {
    reader.addEventListener("load", () => resolve(reader.result), false);
    reader.addEventListener("error", () =>
      reject("Failed to get the base64 from file")
    );

    reader.readAsDataURL(file);
  });
};

const groupEntriesByRevision = groupBy((entry) => entry.revision);

const state = {
  /**
   * entries are a map of TemplateRevId to entries
   */
  entries: {},
  entry: {},
  checkpoint: {
    timestamp: null,
    checklist: null,
  },
  currentLocation: {
    longitude: null,
    latitude: null,
    accuracy: null,
  },
};

const getters = {
  getCheckpoint: (state) => state.checkpoint,
  hasCheckpoint: (state) =>
    state.checkpoint &&
    state.checkpoint.checklist &&
    state.checkpoint.timestamp,
  getAllEntriesFromTemplate: (state) => (templateId) => {
    const keysThatMatch = Object.keys(state.entries).filter((k) =>
      k.includes(templateId)
    );
    let entries = [];
    for (const key of keysThatMatch) {
      entries = [...entries, ...state.entries[key]];
    }
    return entries;
  },
  getEntries: (state) => (templateId, revision) => {
    const templateRevId = TemplateRevId({ templateId, revision });
    if (templateRevId in state.entries) {
      return state.entries[templateRevId];
    }
  },
  getCurrentLocation: (state) => state.currentLocation,
};

const mutations = {
  SET_TEMPLATE_ENTRIES(state, { templateId, revision, checkListEntries }) {
    const templateRevId = TemplateRevId({ templateId, revision });
    state.entries[templateRevId] = checkListEntries;
  },
  ADD_ENTRIES_TO_TEMPLATE(state, { templateId, revision, entries }) {
    const templateRevId = TemplateRevId({ templateId, revision });
    state.entries[templateRevId] = [
      ...entries,
      ...state.entries[templateRevId],
    ];
  },
  CLEAR_TEMPLATE_ENTRIES(state, { templateId }) {
    const entriesToDelete = Object.keys(state.entries).filter((entry) =>
      entry.includes(templateId)
    );

    entriesToDelete.forEach((entry) => delete state.entries[entry]);
  },
  SET_ENTRY(state, entry) {
    state.entry = entry;
  },
  SET_ENTRY_IMAGES(state, images) {
    state.entry.images = images;
  },
  RESET_ENTRY(state) {
    state.entry = {};
  },
  SET_CHECKPOINT(state, checkpoint) {
    state.checkpoint = checkpoint;
  },
  CLEAR_CHECKPOINT(state) {
    state.checkpoint = { timestamp: null, checklist: null };
  },
  SET_CURRENT_LOCATION(state, pos) {
    state.currentLocation = pos;
  },
};

const actions = {
  /**
   * takes the revision in consideration
   */
  async fetchCheckListEntriesFromTemplate(
    { commit, rootGetters, dispatch },
    { templateId, revision, to, from, cursor }
  ) {
    await dispatch("auth/checkUserSession", {}, { root: true });

    const jwtToken = rootGetters["auth/getIdentityToken"];
    const Authorization = `Bearer ${jwtToken}`;

    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    if (cursor) params.cursor = cursor;
    if (revision) params.revision = revision;

    try {
      const response = await checklistClient.get(`/${templateId}`, {
        headers: { Authorization },
        params,
      });

      const data = response.data.results;
      // the templateId of the entry comes as a TemplateRevId
      revision =
        revision !== undefined ? revision : data[0].templateId.split(".")[1];

      const action =
        cursor || response.data.cursor
          ? "ADD_ENTRIES_TO_TEMPLATE"
          : "SET_TEMPLATE_ENTRIES";

      commit(action, {
        templateId,
        revision: revision,
        checkListEntries: data,
      });

      return response.data.cursor;
    } catch (err) {
      /* we silently fail */
    }
  },
  async fetchAllEntriesFromCheckList(
    { dispatch, commit, rootGetters },
    { templateId, from, to, cursor }
  ) {
    await dispatch("auth/checkUserSession", {}, { root: true });

    const jwtToken = rootGetters["auth/getIdentityToken"];
    const Authorization = `Bearer ${jwtToken}`;

    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    if (cursor) params.cursor = cursor;

    try {
      const response = await checklistClient.get(`/${templateId}/entries`, {
        headers: { Authorization },
        params,
      });

      const data = Array.isArray(response.data)
        ? response.data
        : response.data.results;

      if (!data || !data.length) {
        commit("CLEAR_TEMPLATE_ENTRIES", { templateId });
      }

      const entriesByRevision = groupEntriesByRevision(data);
      for (const [rev, entries] of Object.entries(entriesByRevision)) {
        const revision = parseInt(rev);
        const action =
          cursor || response.data.cursor
            ? "ADD_ENTRIES_TO_TEMPLATE"
            : "SET_TEMPLATE_ENTRIES";
        commit(action, {
          templateId,
          revision,
          checkListEntries: entries,
        });
      }

      return data.cursor;
    } catch (err) {
      /* we silently fail */
    }
  },
  async fetchCheckListEntry(
    { commit, rootGetters, dispatch },
    { templateId, revision, entryId }
  ) {
    await dispatch("auth/checkUserSession", {}, { root: true });

    const jwtToken = rootGetters["auth/getIdentityToken"];
    const Authorization = `Bearer ${jwtToken}`;

    const templateRevId = TemplateRevId({ templateId, revision });
    try {
      const response = await checklistClient.get(
        `/${templateRevId}/entries/${entryId}`,
        { headers: { Authorization } }
      );

      commit("SET_ENTRY", response.data);
    } catch (err) {
      /* we fail silentyl */
    }
  },
  async fetchCheckListEntryImages(
    { commit, rootGetters, dispatch },
    { templateId, revision, entryId }
  ) {
    const templateRevId = TemplateRevId({ templateId, revision });

    await dispatch("auth/checkUserSession", {}, { root: true });
    const jwtToken = rootGetters["auth/getIdentityToken"];
    const Authorization = `Bearer ${jwtToken}`;

    try {
      const response = await checklistClient.get(
        `/${templateRevId}/entries/${entryId}/images`,
        { headers: { Authorization } }
      );

      commit("SET_ENTRY_IMAGES", response.data);
    } catch (err) {
      /* we fail silently */
    }
  },
  async createCheckList({ rootGetters, dispatch, getters }, entry) {
    await dispatch("auth/checkUserSession", {}, { root: true });

    entry.createdBy = rootGetters["auth/getUserEmail"];
    entry.completed = false;

    await dispatch("refreshCurrentLocation");

    entry.lastModifiedAt = {
      ...getters.getCurrentLocation,
      timestamp: Date.now(),
    };

    const templateRevId = TemplateRevId({
      templateId: entry.templateId,
      revision: entry.revision,
    });

    const jwtToken = rootGetters["auth/getIdentityToken"];
    const Authorization = `Bearer ${jwtToken}`;

    try {
      const response = await checklistClient.post(
        `/${templateRevId}/entries`,
        entry,
        { headers: { Authorization } }
      );

      return response.data;
    } catch (err) {
      /* TODO: handle */
    }
  },
  async deleteEntry({ dispatch, rootGetters }, { entry }) {
    await dispatch("auth/checkUserSession", {}, { root: true });

    let templateId;
    if (entry.templateId.endsWith(`.${entry.revision}`)) {
      templateId = entry.templateId;
    } else {
      templateId = TemplateRevId({
        templateId: entry.templateId,
        revision: entry.revision,
      });
    }

    const jwtToken = rootGetters["auth/getIdentityToken"];
    const Authorization = `Bearer ${jwtToken}`;

    await checklistClient.delete(`/${templateId}/entries/${entry.entryId}`, {
      headers: { Authorization },
    });
  },
  async setEntryCompleteState({ dispatch, rootGetters }, { entry, completed }) {
    await dispatch("auth/checkUserSession", {}, { root: true });

    let templateId;
    if (entry.templateId.endsWith(`.${entry.revision}`)) {
      templateId = entry.templateId;
    } else {
      templateId = TemplateRevId({
        templateId: entry.templateId,
        revision: entry.revision,
      });
    }

    const jwtToken = rootGetters["auth/getIdentityToken"];
    const Authorization = `Bearer ${jwtToken}`;

    await checklistClient.put(
      `/${templateId}/entries/${entry.entryId}/completed`,
      { completed },
      {
        headers: { Authorization },
      }
    );
  },
  async submitCheckList({ dispatch, rootGetters, getters }, { entry, images }) {
    await dispatch("auth/checkUserSession", {}, { root: true });

    // set the user
    entry.createdBy = rootGetters["auth/getUserEmail"];

    await dispatch("refreshCurrentLocation");
    entry.lastModifiedAt = {
      ...getters.getCurrentLocation,
      timestamp: Date.now(),
    };

    const templateRevId = TemplateRevId({
      templateId: entry.templateId,
      revision: entry.revision,
    });

    const jwtToken = rootGetters["auth/getIdentityToken"];
    const Authorization = `Bearer ${jwtToken}`;

    try {
      const response = await checklistClient.patch(
        `/${templateRevId}/entries/${entry.entryId}`,
        entry,
        { headers: { Authorization } }
      );

      if (!isNil(images) && !isEmpty(images)) {
        for (const image of images) {
          if (isEmpty(image)) continue;
          await dispatch("uploadImage", { image, entry: response.data });
        }
      }
    } catch (err) {
      /* fail silently */
      // TODO: return error
    }
  },
  async uploadImage({ rootGetters }, { image, entry }) {
    const data = {
      name: image.name,
      contentType: image.type,
      encodedContent: await transformImageFileToBase64Data(image),
    };

    const jwtToken = rootGetters["auth/getIdentityToken"];
    const Authorization = `Bearer ${jwtToken}`;

    try {
      await checklistClient.post(
        `/${entry.templateId}/entries/${entry.entryId}/images`,
        data,
        { headers: { Authorization } }
      );
    } catch (err) {
      /* we fail silently... */
      // TODO: retry 3 times then report error
    }
  },
  loadCheckpoint({ commit }) {
    const checkpoint = window.localStorage.getItem("checkpoint");
    if (checkpoint !== null) {
      commit("SET_CHECKPOINT", JSON.parse(checkpoint));
    }
  },
  saveCheckpoint(_, checklist) {
    const timestamp = Date.now();
    window.localStorage.setItem(
      "checkpoint",
      JSON.stringify({
        timestamp,
        checklist,
      })
    );

    return timestamp;
  },
  clearCheckpoint({ commit }) {
    window.localStorage.removeItem("checkpoint");
    commit("CLEAR_CHECKPOINT");
  },
  async refreshCurrentLocation({ commit }) {
    try {
      const currentLocation = await getCurrentPosition();
      commit("SET_CURRENT_LOCATION", {
        longitude: currentLocation.longitude,
        latitude: currentLocation.latitude,
        accuracy: currentLocation.accuracy,
      });
    } catch (error) {
      throw new Error("We require to use the geolocation API");
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
